import React, {useRef} from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {EmojiPickerPopover, Icon, Tooltip} from '@forecast-it/design-system';
import Util from '../../../../util/util';
import AddCommentReactionMutation from '../../../../../../mutations/add_comment_reaction_mutation';
import RemoveCommentReactionMutation from '../../../../../../mutations/remove_comment_reaction_mutation';
import {applyReactionChange, CommentReaction, ReactingPerson} from './commentReactionsUtil';

const QUICK_REACTION = '👍';
const MAX_NAMED_REACTORS = 10;
const MAX_DISTINCT_EMOJIS_PER_COMMENT = 20; // mirrors CommentReactionLogic.MAX_DISTINCT_EMOJIS_PER_COMMENT

type Props = {
	commentId: string;
	reactions: ReadonlyArray<CommentReaction>;
	viewer: ReactingPerson;
	readOnly: boolean;
};

const Row = Styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px;
	margin-top: 4px;
`;

// Same selected-state pattern as ProjectClientPortal.js's RadioCard: brand border + tinted fill
// when selected, decorative border + neutral surface otherwise.
const Chip = Styled.button<{$mine: boolean}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	height: 24px;
	padding: 0 8px;
	border-radius: 12px;
	border: 1px solid
		${props => (props.$mine ? props.theme.sys.color.border.active : props.theme.sys.color.border.decorative)};
	background-color: ${props =>
		props.$mine ? props.theme.sys.color.background.primary['1'] : props.theme.sys.color.background.neutral['1']};
	font-size: 13px;
	cursor: pointer;

	&[aria-disabled='true'] {
		cursor: default;
	}
`;

const Count = Styled.span`
	font-size: 12px;
	color: ${props => props.theme.sys.color.text.content.auxiliary};
`;

const IconButton = Styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	padding: 0;
	border: none;
	border-radius: 12px;
	background: none;
	cursor: pointer;

	&:hover,
	&:focus-visible {
		background-color: ${props => props.theme.sys.color.background.disabled['1']};
	}
`;

export const CommentReactions = ({commentId, reactions, viewer, readOnly}: Props) => {
	const {formatMessage} = useIntl();
	// One emoji's add/remove mutation in flight at a time: a double click on the same chip (or a
	// quick-button/picker click racing a chip click for the same emoji) must not fire twice.
	// Cleared in both onSuccess and onError, so a genuine retry after a failure isn't blocked.
	const pendingEmojis = useRef<Set<string>>(new Set());

	if (readOnly && reactions.length === 0) return null;

	const hasReacted = (emoji: string) => reactions.some(reaction => reaction.emoji === emoji && reaction.viewerHasReacted);
	// The server rejects a 21st distinct emoji on a comment, but always allows joining one already
	// there — so the cap only ever needs to hide ways to add a *new* emoji, never the chips themselves.
	const atDistinctCap = reactions.length >= MAX_DISTINCT_EMOJIS_PER_COMMENT;

	const commit = (emoji: string, add: boolean) => {
		if (pendingEmojis.current.has(emoji)) return;
		pendingEmojis.current.add(emoji);
		const clearPending = () => pendingEmojis.current.delete(emoji);

		Util.CommitMutation(
			add ? AddCommentReactionMutation : RemoveCommentReactionMutation,
			{
				commentId,
				emoji,
				optimisticReactions: applyReactionChange(reactions, emoji, viewer, add),
			},
			clearPending,
			true,
			null,
			(transaction, onFailure) => {
				// onFailure returns whether it scheduled the one automatic retry on a transient
				// network error (same contract used in comment_section_comment.js's saveComment).
				// That retry re-invokes CommitMutation with this same onSuccess/onError, so clearing
				// the guard here too would leave its ~500ms window unguarded — a click in that
				// window would start a second, concurrent mutation for the same emoji. Only clear
				// when this attempt is truly done; the retry's own onSuccess/onError clears it.
				const retrying = onFailure(transaction);
				if (!retrying) clearPending();
			}
		);
	};

	// The picker only ever adds: choosing an emoji you already reacted with must not quietly take it away.
	const onPick = (emoji: string) => {
		if (!hasReacted(emoji)) commit(emoji, true);
	};

	const quickLabel = formatMessage({id: 'task_modal.comment_reactions.quick'}, {emoji: QUICK_REACTION});
	const addLabel = formatMessage({id: 'task_modal.comment_reactions.add'});

	const describeReactors = (reaction: CommentReaction) => {
		if (reaction.persons.length === 0) {
			// Every listed reactor failed to load: there is no name left to show, so fall back to the
			// same "{emoji}, N reactions" text as the chip's own aria-label rather than a tooltip that
			// reads "and 3 others" with nothing named before it.
			return formatMessage({id: 'task_modal.comment_reactions.chip'}, {emoji: reaction.emoji, count: reaction.count});
		}
		// `count` is authoritative: the server lists at most 10 reactors, and a reactor who fails to load is
		// dropped from `persons`, so the "and N others" tail is count minus the names actually shown.
		const names = reaction.persons
			.slice(0, MAX_NAMED_REACTORS)
			.map(person => person.fullName)
			.join(', ');
		const others = reaction.count - Math.min(reaction.persons.length, MAX_NAMED_REACTORS);
		// One ICU message, so each locale controls the word order around the names.
		return others > 0
			? formatMessage({id: 'task_modal.comment_reactions.reactors_and_more'}, {names, count: others})
			: names;
	};

	return (
		<Row data-cy="comment-reactions">
			{reactions.map(reaction => (
				// `interactable`: the design-system Tooltip otherwise swallows the chip's click.
				<Tooltip key={reaction.emoji} interactable content={describeReactors(reaction)}>
					<Chip
						type="button"
						data-cy="comment-reaction-chip"
						$mine={reaction.viewerHasReacted}
						aria-pressed={reaction.viewerHasReacted}
						// aria-disabled, not disabled: a disabled button gets no hover, and read-only
						// viewers still get to see who reacted.
						aria-disabled={readOnly}
						aria-label={formatMessage(
							{id: 'task_modal.comment_reactions.chip'},
							{emoji: reaction.emoji, count: reaction.count}
						)}
						onClick={() => {
							if (!readOnly) commit(reaction.emoji, !reaction.viewerHasReacted);
						}}
					>
						<span aria-hidden="true">{reaction.emoji}</span>
						<Count aria-hidden="true">{reaction.count}</Count>
					</Chip>
				</Tooltip>
			))}
			{readOnly ? null : (
				<>
					{/* Joining an existing 👍 is still allowed at the cap; only a *new* 👍 would be a 21st emoji. */}
					{hasReacted(QUICK_REACTION) ||
					(atDistinctCap && !reactions.some(r => r.emoji === QUICK_REACTION)) ? null : (
						<Tooltip interactable content={quickLabel}>
							<IconButton
								type="button"
								data-cy="comment-reaction-quick"
								aria-label={quickLabel}
								onClick={() => commit(QUICK_REACTION, true)}
							>
								<Icon icon="like" size="s" />
							</IconButton>
						</Tooltip>
					)}
					{atDistinctCap ? null : (
						// Tooltip wraps the whole EmojiPickerPopover, not just its `trigger` prop:
						// EmojiPickerPopover forwards the ref and handlers the outer Tooltip hands it down to
						// its own Popover.Trigger(asChild), so both the popover's click-to-open and the
						// tooltip's hover/focus wiring land on the same IconButton. Nesting the Tooltip inside
						// `trigger` instead would put it between Popover.Trigger and the button, where
						// EmojiPickerPopover never forwards anything onto it.
						<Tooltip interactable content={addLabel}>
							<EmojiPickerPopover
								data-cy="comment-reaction-picker"
								onSelectEmoji={onPick}
								trigger={
									<IconButton type="button" data-cy="comment-reaction-add" aria-label={addLabel}>
										<Icon icon="emoji" size="s" />
									</IconButton>
								}
							/>
						</Tooltip>
					)}
				</>
			)}
		</Row>
	);
};
